import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, ConfirmModal, useRole, formatDate } from '../components/shared';
import { everyLabel, visitBadge, todayStr } from '../components/PmLog';
import { ContractForm, CONTRACT_STATUS } from './Maintenance';
import Photo from '../components/Photo';
import PdfReport from '../components/PdfReport';

const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';
const dayAfter = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d + 1).toLocaleDateString('en-CA'); };
const everyVisits = n => (n <= 1 ? 'كل زيارة' : n === 2 ? 'كل زيارتين' : `كل ${n} زيارات`);

function TasksEditor({ cd, onSaved, onCancel }) {
  const [rows, setRows] = useState(cd.tasks.map(t => ({ text: t.text, every_visits: t.every_visits })));
  const toast = useToast();
  const setRow = (i, k, v) => setRows(r => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  async function save() {
    try { await api.pmSetTasks(cd.id, rows.filter(r => r.text.trim())); toast('اتحفظت المهام'); onSaved(); }
    catch (e) { toast(e.message, 'error'); }
  }
  return (
    <div className="mt-8">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-8 mb-8 items-center">
          <input className="input" style={{ flex: 1 }} value={r.text} onChange={e => setRow(i, 'text', e.target.value)} placeholder="المهمة" />
          <select className="select" style={{ width: 130 }} value={r.every_visits} onChange={e => setRow(i, 'every_visits', Number(e.target.value))}>
            {[1, 2, 3, 4, 6, 12].map(n => <option key={n} value={n}>{everyVisits(n)}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => setRows(x => x.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <div className="flex gap-8">
        <button className="btn btn-ghost btn-sm" onClick={() => setRows(x => [...x, { text: '', every_visits: 1 }])}>+ مهمة</button>
        <button className="btn btn-primary btn-sm" style={{ marginInlineStart: 'auto' }} onClick={save}>حفظ</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function DeviceInfoEditor({ cd, onSaved, onCancel }) {
  const [f, setF] = useState({ name: cd.name, serial: cd.serial.startsWith('NOSN-') ? '' : cd.serial, alias: cd.alias || '', notes: cd.notes_device || '' });
  const toast = useToast();
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  async function save() {
    if (!f.name.trim()) return toast('اسم الجهاز مطلوب', 'error');
    try {
      await api.updateDevice(cd.device_id, { name: f.name, serial: f.serial.trim() || cd.serial, alias: f.alias, notes: f.notes });
      toast('اتحفظ'); onSaved();
    } catch (e) { toast(e.message, 'error'); }
  }
  return (
    <div className="mt-8" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
      <input className="input" placeholder="اسم الجهاز" value={f.name} onChange={set('name')} autoFocus />
      <input className="input" dir="ltr" placeholder="Serial" value={f.serial} onChange={set('serial')} />
      <input className="input" placeholder="اسم مميز" value={f.alias} onChange={set('alias')} />
      <input className="input" placeholder="ملاحظات" value={f.notes} onChange={set('notes')} />
      <div className="flex gap-8">
        <button className="btn btn-primary btn-sm" onClick={save}>حفظ</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function AddDevice({ contract, onAdded }) {
  const free = contract.center_devices.filter(d => !contract.devices.some(cd => cd.device_id === d.id));
  const [pick, setPick] = useState('');
  const [nd, setNd] = useState({ name: '', serial: '', alias: '' });
  const toast = useToast();
  async function add(body) {
    try { await api.pmAddContractDevice(contract.id, body); setPick(''); setNd({ name: '', serial: '', alias: '' }); onAdded(); }
    catch (e) { toast(e.message, 'error'); }
  }
  return (
    <div className="card mb-16" style={{ padding: 12 }}>
      <div className="fw-600 text-sm mb-8">+ إضافة جهاز للعقد</div>
      {free.length > 0 && (
        <div className="flex gap-8 mb-8">
          <select className="select" value={pick} onChange={e => setPick(e.target.value)}>
            <option value="">من أجهزة المركز ({free.length})</option>
            {free.map(d => <option key={d.id} value={d.id}>{d.name} — {d.serial}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" disabled={!pick} onClick={() => add({ device_id: pick })}>إضافة</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr auto', gap: 8 }}>
        <input className="input" placeholder="جهاز جديد: الاسم" value={nd.name} onChange={e => setNd({ ...nd, name: e.target.value })} />
        <input className="input" dir="ltr" placeholder="Serial" value={nd.serial} onChange={e => setNd({ ...nd, serial: e.target.value })} />
        <input className="input" placeholder="اسم مميز" value={nd.alias} onChange={e => setNd({ ...nd, alias: e.target.value })} />
        <button className="btn btn-primary btn-sm" disabled={!nd.name.trim()} onClick={() => add({ new_device: nd })}>إضافة</button>
      </div>
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState('devices');
  const [modal, setModal] = useState(null); // 'edit' | 'renew' | 'new'
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef(null);
  const toast = useToast();
  const role = useRole();
  const isAdmin = role === 'admin';
  const canPhoto = role === 'admin' || role === 'technician';
  const navigate = useNavigate();

  const load = useCallback(() => api.pmContract(id).then(setC).catch(e => toast(e.message, 'error')), [id, toast]);
  useEffect(() => { load(); api.getClients().then(setClients).catch(() => {}); }, [load]);
  if (!c) return <div className="page"><div className="empty">Loading...</div></div>;

  const [sl, scls] = CONTRACT_STATUS[c.status] || [c.status, 'badge-gray'];
  async function saveEdit(body) { await api.pmUpdateContract(c.id, body); toast('اتحفظ'); setModal(null); load(); }
  async function duplicate(body) {
    const r = await api.pmDuplicateContract(c.id, { ...body, mode: modal });
    toast('اتعمل نسخة'); setModal(null); navigate(`/maintenance/${r.id}`);
  }
  async function upload(e) {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast('الملف أكبر من 20MB', 'error');
    try { await api.pmUploadContractFile(c.id, file); toast('اترفع العقد'); load(); } catch (err) { toast(err.message, 'error'); }
  }
  const dupInitial = { ...c, start_date: dayAfter(c.end_date), end_date: '', client_id: '', title: c.title || '' };

  return (
    <div className="page" dir="rtl">
      <button className="btn btn-ghost btn-sm mb-16" onClick={() => navigate('/maintenance')}>→ كل العقود</button>

      <div className="card mb-16" style={{ padding: 18, borderTop: '4px solid var(--primary)' }}>
        <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div className="flex items-center gap-12">
            <Photo kind="client" id={c.client_id} version={c.client_photo_at} size={88} icon="🏥" canEdit={canPhoto} canDelete={isAdmin} onChange={load} />
            <div>
              <div className="fw-600" style={{ fontSize: 22 }}>{c.client_name}</div>
              {c.title && <div className="text-muted">{c.title}</div>}
            </div>
          </div>
          <span className={`badge ${scls}`}>{sl}</span>
        </div>
        <div className="flex gap-12 mt-8 text-sm" style={{ flexWrap: 'wrap' }}>
          <span>📅 {formatDate(c.start_date)} ← {formatDate(c.end_date)}</span>
          <span>🔁 زيارة {everyLabel(c.every_value, c.every_unit)}</span>
          <span>💰 {money(c.value)}</span>
          {c.assignee && <span>👤 {c.assignee}</span>}
          {c.client_phone && <a className="link" href={`tel:${c.client_phone}`}>📞 {c.client_phone}</a>}
        </div>
        {c.notes && <div className="text-sm text-muted mt-8" style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</div>}
        <div className="flex gap-8 mt-16" style={{ flexWrap: 'wrap' }}>
          {c.file_name
            ? <button className="btn btn-ghost btn-sm" onClick={() => api.pmOpenContractFile(c.id).catch(e => toast(e.message, 'error'))}>📎 {c.file_name}</button>
            : <span className="text-xs text-dim" style={{ alignSelf: 'center' }}>مفيش ملف عقد مرفوع</span>}
          {isAdmin && <>
            <input ref={fileRef} type="file" accept=".pdf,image/*,.doc,.docx" style={{ display: 'none' }} onChange={upload} />
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current.click()}>{c.file_name ? '⤴ تغيير الملف' : '⤴ ارفع العقد'}</button>
            {c.file_name && <button className="btn btn-ghost btn-sm" onClick={() => api.pmDeleteContractFile(c.id).then(load)}>🗑 الملف</button>}
            <span style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}>✏️ تعديل</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('renew')}>📑 تجديد</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('new')}>📑 نسخ لمركز تاني</button>
          </>}
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/maintenance/labels?contract=${c.id}`)}>🏷 QR</button>
          <PdfReport kind="month" id={c.id} />
          {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ kind: 'contract' })}>حذف</button>}
        </div>
      </div>

      <div className="stats-grid mb-16">
        <div className="stat-card"><div className="stat-label">أجهزة</div><div className="stat-value">{c.device_count}</div></div>
        <div className="stat-card"><div className="stat-label">زيارات تمت</div><div className="stat-value">{c.visits_done}/{c.visits_total}</div></div>
        <div className="stat-card"><div className="stat-label">متأخرة</div><div className={`stat-value ${c.visits_overdue ? 'text-red' : ''}`}>{c.visits_overdue}</div></div>
        <div className="stat-card"><div className="stat-label">الزيارة الجاية</div><div className="stat-value" style={{ fontSize: 18 }}>{c.next_visit ? formatDate(c.next_visit) : '—'}</div></div>
      </div>

      <div className="flex gap-8 mb-16">
        <button className={`btn btn-sm ${tab === 'devices' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('devices')}>📟 الأجهزة ({c.devices.length})</button>
        <button className={`btn btn-sm ${tab === 'visits' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('visits')}>📅 الزيارات ({c.visits.length})</button>
      </div>

      {tab === 'devices' && <>
        {isAdmin && <AddDevice contract={c} onAdded={load} />}
        {c.devices.length === 0 && <div className="card"><div className="empty">مفيش أجهزة في العقد</div></div>}
        {c.devices.map(cd => (
          <div key={cd.id} className="card mb-8" style={{ padding: 14 }}>
            <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
              <Photo kind="device" id={cd.device_id} version={cd.photo_at} size={64} icon="📟" canEdit={canPhoto} canDelete={isAdmin} onChange={load} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <span className="fw-600">{cd.name}</span>{cd.alias && <span className="text-muted"> · {cd.alias}</span>}
                <div className={`mono text-xs ${cd.serial.startsWith('NOSN-') ? 'text-red' : 'text-dim'}`} dir="ltr" style={{ textAlign: 'right' }}>
                  {cd.serial.startsWith('NOSN-') ? '⚠ محتاج سيريال · ' : ''}{cd.serial}</div>
                {cd.notes_device && <div className="text-xs text-muted">{cd.notes_device}</div>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/d/${cd.qr_token}`)}>📱 صفحة الجهاز</button>
              {isAdmin && editing !== 'info' + cd.id && <button className="btn btn-ghost btn-sm" onClick={() => setEditing('info' + cd.id)}>✏️ البيانات</button>}
              {isAdmin && editing !== cd.id && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(cd.id)}>✏️ المهام</button>}
              {isAdmin && <button className="btn btn-ghost btn-sm" title="نسخ مهام الجهاز ده لكل الأجهزة"
                onClick={() => setConfirm({ kind: 'apply', cd })}>⇉ للكل</button>}
              {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ kind: 'device', cd })}>شيل</button>}
            </div>
            {editing === 'info' + cd.id && <DeviceInfoEditor cd={cd} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
            {editing === cd.id
              ? <TasksEditor cd={cd} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
              : <div className="flex gap-8 mt-8" style={{ flexWrap: 'wrap' }}>
                  {cd.tasks.map(t => (
                    <span key={t.id} className="text-xs" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '3px 10px' }}>
                      {t.text} <span className="text-dim">· {everyVisits(t.every_visits)}</span></span>
                  ))}
                </div>}
          </div>
        ))}
      </>}

      {tab === 'visits' && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>#</th><th>الميعاد</th><th>الحالة</th><th>الأجهزة</th><th>إصلاحات</th><th>الفواتير</th><th>تمت</th><th></th></tr></thead>
            <tbody>
              {c.visits.map(v => {
                const [l, cls] = visitBadge(v);
                return (
                  <tr key={v.id}>
                    <td className="mono">{v.seq}</td>
                    <td>{isAdmin && v.status !== 'done'
                      ? <input type="date" className="input" style={{ width: 'auto', padding: '2px 6px' }} value={v.scheduled_date}
                          onChange={e => e.target.value && api.pmUpdateVisit(v.id, { scheduled_date: e.target.value }).then(load)} />
                      : formatDate(v.scheduled_date)}</td>
                    <td><span className={`badge ${cls}`}>{l}</span>{v.issues > 0 && <span className="badge badge-yellow" style={{ marginInlineStart: 4 }}>⚠ {v.issues}</span>}</td>
                    <td className="mono text-sm">{v.logged}/{c.device_count}</td>
                    <td className="mono text-sm">{v.repairs ? `🔧 ${v.repairs}` : '—'}</td>
                    <td className="mono text-sm">{v.invoiced ? <span className={v.paid >= v.invoiced ? 'text-green' : ''}>{Number(v.invoiced).toLocaleString('en')}{v.paid < v.invoiced ? <span className="text-xs text-dim"> (اتدفع {Number(v.paid).toLocaleString('en')})</span> : ' ✓'}</span> : '—'}</td>
                    <td className="text-sm text-muted">{v.done_at ? formatDate(v.done_at) : '—'}</td>
                    <td><div className="flex gap-8">
                      <button className="btn btn-primary btn-sm" onClick={() => navigate(`/maintenance/visit/${v.id}`)}>فتح</button>
                      {isAdmin && v.status === 'planned' && <button className="btn btn-ghost btn-sm" onClick={() => api.pmUpdateVisit(v.id, { status: 'missed' }).then(load)}>إلغاء</button>}
                      {isAdmin && v.status === 'missed' && <button className="btn btn-ghost btn-sm" onClick={() => api.pmUpdateVisit(v.id, { status: 'planned' }).then(load)}>رجّعها</button>}
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal === 'edit' && <ContractForm title="تعديل العقد" mode="edit" initial={c} clients={clients} onSave={saveEdit} onClose={() => setModal(null)} />}
      {modal === 'renew' && <ContractForm title={`تجديد عقد ${c.client_name}`} mode="renew" initial={dupInitial} clients={clients} onSave={duplicate} onClose={() => setModal(null)} />}
      {modal === 'new' && <ContractForm title="نسخ العقد لمركز تاني" mode="new" initial={{ ...dupInitial, start_date: todayStr() }} clients={clients.filter(x => x.id !== c.client_id)}
        devices={c.devices} onSave={duplicate} onClose={() => setModal(null)} />}
      {confirm && <ConfirmModal
        message={confirm.kind === 'contract' ? 'حذف العقد بكل زياراته وسجله؟ (الأجهزة نفسها مش هتتمسح)'
          : confirm.kind === 'device' ? `شيل ${confirm.cd.name} من العقد؟` : `نسخ مهام ${confirm.cd.name} لكل أجهزة العقد؟ (هتستبدل مهامهم)`}
        onConfirm={async () => {
          try {
            if (confirm.kind === 'contract') { await api.pmDeleteContract(c.id); navigate('/maintenance'); return; }
            if (confirm.kind === 'device') await api.pmRemoveContractDevice(confirm.cd.id);
            if (confirm.kind === 'apply') await api.pmApplyTasksToAll(confirm.cd.id);
            setConfirm(null); load();
          } catch (e) { toast(e.message, 'error'); }
        }} onClose={() => setConfirm(null)} />}
    </div>
  );
}
